import { AppId, getAppUrl } from '@shared/contexts/ConfigContext';
import { AppNav, AppNavItemKeys, ConfigCore } from '@shared/models/config';
import { history } from '@shared/utils/history';

interface RouteObject {
  readonly [key: string]: RouteValue;
}

type RouteFn = (...args: any[]) => string;

type RouteValue = string | RouteFn | RouteObject;

const ignoredKeys = ['listDefault'];

export function generateRoutePaths(routeNode: RouteValue, basePath = '', routesList: string[] = []): string[] {
  if (typeof routeNode === 'string') {
    if (routeNode) {
      routesList.push(basePath + routeNode);
    }
  }

  if (typeof routeNode === 'object') {
    let currentPath = basePath;
    if (typeof routeNode.root === 'string') {
      currentPath = routeNode.root;
    } else if (typeof routeNode.rootPath === 'string') {
      currentPath = routeNode.rootPath;
    }

    Object.keys(routeNode).forEach((key) => {
      if (ignoredKeys.includes(key)) {
        return;
      }

      const childNode = routeNode[key as keyof typeof routeNode];

      if (childNode) {
        let newBasePath = basePath;
        if (key !== 'root' && key !== 'rootPath') {
          newBasePath = currentPath;
        }
        generateRoutePaths(childNode, newBasePath, routesList);
      }
    });
  }

  return routesList;
}

export const getDefaultPath = (appUrl: string, appNav: AppNav) => {
  const defaultPageItem = appNav[appNav.default_page as AppNavItemKeys];
  const defaultUrl = defaultPageItem?.url ?? '/';

  return getTransformedRoute(appUrl, defaultUrl);
};

// Carries the current URL's query params through to a redirect target, keeping
// only the keys shouldCarry accepts so the rest of the app's URL state is
// unaffected. Third party integrations (jam.dev today) hand us params their SDK
// reads off the landing URL, and those must survive our default redirects.
export const carrySearchParams = (url: string, shouldCarry: (key: string) => boolean) => {
  const carried = new URLSearchParams();

  new URLSearchParams(window.location.search).forEach((value, key) => {
    if (shouldCarry(key)) {
      carried.append(key, value);
    }
  });

  const search = carried.toString();
  if (!search) {
    return url;
  }

  return `${url}${url.includes('?') ? '&' : '?'}${search}`;
};

export interface ReactUrlNavigation {
  config: ConfigCore;
  currentAppId: AppId;
  newTab?: boolean;
  path: string;
  targetAppId: AppId;
}

export const getReactUrl = ({ config, currentAppId, path, targetAppId }: ReactUrlNavigation) => {
  if (currentAppId === targetAppId) {
    return path;
  }

  return `${getAppUrl(targetAppId, config)}${path}`;
};

export const navigateToReactUrl = (navigation: ReactUrlNavigation) => {
  const url = getReactUrl(navigation);

  if (navigation.newTab || navigation.currentAppId !== navigation.targetAppId) {
    window.open(url, '_blank', 'noopener');
  } else {
    history.push(url);
  }
};

export const getTransformedRoute = (appUrl: string, url: string): string => {
  let transformedUrl = url;

  if (transformedUrl.startsWith(appUrl)) {
    transformedUrl = transformedUrl.slice(appUrl.length);
  }

  transformedUrl = transformedUrl.replace('/atlas-rankings', '/atlas-data');

  return transformedUrl;
};

export const generateUrlId = (url: string) => {
  // Strip url prefixes
  if (url.includes('://')) {
    // Strip any protocols, i.e. keep after ://
    url = url.split('://')[1] ?? '';

    // Strip anything before first slash
    if (url.includes('/')) {
      url = url.split('/').slice(1).join('/');
    }
  }

  // Remove hash
  if (url.includes('#')) {
    url = url.split('#')[1] ?? '';
  }

  // Remove query string
  if (url.includes('?')) {
    url = url.split('?')[0] ?? '';
  }

  // Replace - with _
  url = url.replace(/-/g, '_');

  const chunks = url.split('/').reduce((result, chunk) => {
    const parsedNumber = parseInt(chunk, 10);

    if (chunk && isNaN(parsedNumber)) {
      // Not a number, must be a string
      result.push(chunk);
    } else if (chunk) {
      // Valid number, push id
      result.push('id');
    }

    return result;
  }, [] as string[]);

  return chunks.join('_');
};
